import { ClipboardCheck } from 'lucide-react';
import type { ExecutionStatusSummary as SummaryType } from '@monitoring-panel/types';
import { Skeleton } from './Skeleton';
import { cn } from '@/lib/utils';

interface ExecutionStatusSummaryProps {
  summary: SummaryType | undefined;
  isLoading: boolean;
  onChecklistClick: () => void;
}

/**
 * Compact summary badge for checklist progress.
 * Shown on each CampaignCard header area.
 */
export function ExecutionStatusSummary({
  summary,
  isLoading,
  onChecklistClick,
}: ExecutionStatusSummaryProps) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-3">
        <Skeleton className="h-5 w-20 rounded-full" />
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="flex items-center gap-3 text-adv-body-s text-adv-tertiary">
        <span>No status</span>
      </div>
    );
  }

  const { checklist } = summary;
  const effectiveTotal = checklist.total - checklist.na;
  const checklistDone = checklist.done;

  // Color coding for checklist: all done → green, partial → amber, none → gray
  const checklistColor =
    effectiveTotal === 0
      ? 'adv-tag--default'
      : checklistDone === effectiveTotal
        ? 'adv-tag--success'
        : checklistDone > 0
          ? 'adv-tag--gold'
          : 'adv-tag--default';

  // Checklist progress bar width
  const checklistProgress =
    effectiveTotal > 0 ? (checklistDone / effectiveTotal) * 100 : 0;
  const checklistBarColor =
    checklistDone === effectiveTotal && effectiveTotal > 0
      ? 'bg-adv-success'
      : checklistDone > 0
        ? 'bg-adv-gold'
        : 'bg-adv-border-strong';

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Checklist Badge */}
      <button
        onClick={onChecklistClick}
        className={cn(
          'adv-tag transition-opacity',
          'hover:opacity-80 min-h-[28px] focus-visible:outline-none focus-visible:shadow-adv-focus',
          checklistColor,
        )}
        title={`Checklist: ${checklistDone}/${effectiveTotal} done`}
      >
        <ClipboardCheck size={12} className="flex-shrink-0" />
        <span className="adv-num">{checklistDone}/{effectiveTotal}</span>
        {/* Mini progress bar */}
        <div className="w-8 h-1.5 rounded-full bg-adv-border-default overflow-hidden">
          <div
            className={cn('h-full rounded-full transition-all', checklistBarColor)}
            style={{ width: `${checklistProgress}%` }}
          />
        </div>
      </button>

    </div>
  );
}
